import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, TrendingDown, FileText, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MedicoDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [patientEmail, setPatientEmail] = useState('');

  useEffect(() => {
    if (user?.role !== 'doctor') {
      navigate('/');
      return;
    }
    fetchPatients();
  }, [user, navigate]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/doctor/patients`);
      setPatients(response.data);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
      toast.error('Erro ao carregar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPatient = () => {
    toast.info('Compartilhe seu ID com o paciente para que ele se cadastre vinculado a você');
    toast.info(`Seu ID de médico: ${user?.id}`);
    setShowAddPatient(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Meus Pacientes
            </h2>
            <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Gerencie e acompanhe seus pacientes
            </p>
          </div>
          <button
            data-testid="add-patient-button"
            onClick={handleAddPatient}
            className="bg-primary text-primary-foreground rounded-full px-6 py-3 font-bold shadow-lg hover:shadow-xl transition-all"
          >
            Adicionar Paciente
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="text-primary" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total de Pacientes</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {patients.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
                <FileText className="text-secondary-foreground" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Prescrições Ativas</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  --
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                <TrendingDown className="text-accent" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Média de Progresso</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  --
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Patients List */}
        {patients.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto text-muted-foreground mb-4" size={64} />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nenhum Paciente Cadastrado
            </h3>
            <p className="text-muted-foreground mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Comece adicionando seu primeiro paciente
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {patients.map((patient) => (
              <div
                key={patient.id}
                data-testid={`patient-card-${patient.id}`}
                onClick={() => navigate(`/medico/paciente/${patient.id}`)}
                className="bg-card rounded-2xl shadow-sm border border-border p-6 cursor-pointer hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="text-primary" size={28} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {patient.nome}
                      </h3>
                      <p className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {patient.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {patient.peso_inicial && patient.peso_meta && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Meta</p>
                        <p className="text-lg font-bold text-primary">
                          {patient.peso_inicial} → {patient.peso_meta} kg
                        </p>
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/chat/${patient.id}`);
                      }}
                      className="p-3 bg-secondary rounded-full hover:bg-secondary/80 transition-colors"
                      data-testid={`chat-button-${patient.id}`}
                    >
                      <MessageCircle className="text-secondary-foreground" size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MedicoDashboard;